import React, { useState } from 'react';
import { 
  Sparkles, 
  Copy, 
  RotateCcw, 
  Send, 
  Languages, 
  Highlighter, 
  Eraser, 
  Check, 
  ArrowLeft 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/db/supabase';

const CopyPolishingPage: React.FC = () => {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [isPolishing, setIsPolishing] = useState(false);
  const [style, setStyle] = useState('小红书风');

  const handlePolish = async () => {
    if (!input.trim()) {
      toast.error('请输入文案内容');
      return;
    }

    setIsPolishing(true);
    try {
      const { data, error } = await supabase.functions.invoke('ai_text_generation', {
        body: {
          messages: [
            { role: 'user', content: `请将以下文案改写为${style}格式：\n\n${input}` }
          ],
          task_type: 'polishing'
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      setOutput(data.content || '生成失败，请重试');
      toast.success('润色成功');
    } catch (err: any) {
      console.error('AI polishing error:', err);
      toast.error(`润色失败: ${err.message}`);
    } finally {
      setIsPolishing(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(output);
    toast.success('已复制到剪贴板');
  };

  return (
    <div className="min-h-screen bg-background pb-20 animate-fade-in">
      <div className="tech-flow-bg h-[160px] flex items-center px-6">
        <div className="container mx-auto">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/10 mb-2 gap-2"
            onClick={() => navigate('/ai-hub')}
          >
            <ArrowLeft className="h-4 w-4" />
            返回 AI 助手
          </Button>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2">
            <Sparkles className="h-6 w-6" />
            AI 文案润色
          </h1>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="rounded-xl glass-card border-border/50 shadow-lg">
            <CardHeader className="p-4 border-b border-border/50 flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-primary font-bold">
                输入原文
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setInput('')} className="text-muted-foreground h-8 gap-1">
                <Eraser className="h-3 w-3" /> 清空
              </Button>
            </CardHeader>
            <CardContent className="p-4 pt-6 space-y-6">
              <Textarea 
                placeholder="在此输入您的文案原文..." 
                className="min-h-[300px] rounded-lg border-primary/20 bg-background/50 resize-none text-base leading-relaxed p-4 focus-visible:ring-primary"
                value={input}
                onChange={(e) => setInput(e.target.value)}
              />
              
              <div className="space-y-3">
                <h4 className="text-sm font-bold text-muted-foreground">选择风格</h4>
                <div className="flex flex-wrap gap-2">
                  {['小红书风', '商务风', '幽默风', '学术风', '诗意风'].map((s) => (
                    <Badge 
                      key={s} 
                      variant={style === s ? 'default' : 'outline'}
                      className={`cursor-pointer px-4 py-1.5 rounded-full transition-all duration-300 ${style === s ? 'bg-primary text-white shadow-glow-sm' : 'hover:border-primary/50'}`}
                      onClick={() => setStyle(s)}
                    >
                      {s}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
            <CardFooter className="p-4 pt-2">
              <Button 
                className="w-full bg-primary text-white hover:bg-primary/90 h-12 shadow-lg btn-ripple flex items-center gap-2 font-bold"
                onClick={handlePolish}
                disabled={isPolishing}
              >
                {isPolishing ? (
                  <>
                    <RotateCcw className="h-4 w-4 animate-spin" />
                    正在深度润色中...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    立即润色
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>

          <Card className="rounded-xl glass-card border-border/50 shadow-lg bg-gradient-to-br from-primary/5 to-secondary/5">
            <CardHeader className="p-4 border-b border-border/50 flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-primary font-bold">
                润色结果
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 gap-1 text-primary hover:bg-primary/10"
                  onClick={handleCopy}
                  disabled={!output}
                >
                  <Copy className="h-3 w-3" /> 复制
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-6">
              <div className="min-h-[300px] w-full rounded-lg border border-primary/10 bg-white/50 p-6 whitespace-pre-wrap text-base leading-relaxed relative overflow-y-auto max-h-[450px]">
                {output ? (
                  output
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground opacity-30">
                    <Highlighter className="h-16 w-16 mb-4" />
                    <p className="text-sm font-medium">润色结果将在此显示</p>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="p-4 pt-2">
              <div className="w-full grid grid-cols-3 gap-3">
                <Button 
                  variant="outline" 
                  className="gap-1.5 h-10 border-primary/20 text-primary hover:bg-primary/5 font-bold" 
                  disabled={!output}
                  onClick={() => {}}
                >
                  <RotateCcw className="h-3.5 w-3.5" /> 重新生成
                </Button>
                <Button 
                  variant="outline" 
                  className="gap-1.5 h-10 border-secondary/20 text-secondary hover:bg-secondary/5 font-bold" 
                  disabled={!output}
                  onClick={() => {}}
                >
                  <Languages className="h-3.5 w-3.5" /> 译为英文
                </Button>
                <Button 
                  className="gap-1.5 h-10 bg-secondary text-white hover:bg-secondary/90 shadow-md font-bold" 
                  disabled={!output}
                  onClick={() => {}}
                >
                  <Send className="h-3.5 w-3.5" /> 立即发布
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CopyPolishingPage;
